import React, { useEffect, useState } from 'react';
import { api } from '../api';

const r0 = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const pct = (a, b) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
const rs2 = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const qty = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const istToday = () => new Date(Date.now() + 1.5 * 3600000).toISOString().slice(0, 10);

export default function DayBreakdown() {
  const [date, setDate] = useState(istToday());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    const onPick = (e) => { if (e.detail) setDate(e.detail); };
    window.addEventListener('dp-day', onPick);
    return () => window.removeEventListener('dp-day', onPick);
  }, []);

  useEffect(() => {
    setError('');
    api.get(`/profit/day-items?date=${date}`).then((d) => { setData(d); setOpen({}); }).catch((e) => setError(e.message));
  }, [date]);

  const S = {
    wrap: { marginTop: 18, padding: 16, border: '1px solid #ece6f2', borderRadius: 16, background: '#fff', fontFamily: "'Manrope', system-ui, sans-serif", color: '#231a33' },
    head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
    th: { textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#776e86', padding: '8px 10px', borderBottom: '1px solid #ece6f2', whiteSpace: 'nowrap' },
    td: { textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #f4f0f8', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  };
  const left = { textAlign: 'left' };

  return (
    <div style={S.wrap} className="dp-breakdown">
      <div style={S.head}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17 }}>Sales &amp; cost of goods breakdown</h3>
          <div style={{ fontSize: 13, color: '#776e86' }}>Tap a day in the table above, or pick a date. Tap a category to see its products.</div>
        </div>
        <input type="date" value={date} max={istToday()} onChange={(e) => e.target.value && setDate(e.target.value)}
          style={{ height: 40, padding: '0 10px', border: '1px solid #e2dcea', borderRadius: 10, font: 'inherit' }} />
      </div>
      {error && <div style={{ color: '#d63d5b', fontWeight: 700 }}>{error}</div>}
      {data && data.categories.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#776e86' }}>No sales on this day.</div>}
      {data && data.categories.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ ...S.th, ...left }}>Category / product</th>
                <th style={S.th}>Qty</th>
                <th style={S.th}>MRP / pc</th>
                <th style={S.th}>Sales</th>
                <th style={S.th}>Cost / pc</th>
                <th style={S.th}>Cost of goods</th>
                <th style={S.th}>Gross profit</th>
                <th style={S.th}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {data.categories.map((c) => (
                <React.Fragment key={c.category}>
                  <tr onClick={() => setOpen((o) => ({ ...o, [c.category]: !o[c.category] }))} style={{ cursor: 'pointer', background: open[c.category] ? '#f6f3ff' : 'transparent' }}>
                    <td style={{ ...S.td, ...left, fontWeight: 800 }}><span style={{ color: '#5b3fd9', display: 'inline-block', width: 16 }}>{open[c.category] ? '▾' : '▸'}</span>{c.category} <small style={{ color: '#8b8299', fontWeight: 600 }}>({c.items.length})</small></td>
                    <td style={S.td}>—</td>
                    <td style={S.td} />
                    <td style={{ ...S.td, fontWeight: 800 }}>{r0(c.sales)}</td>
                    <td style={S.td} />
                    <td style={S.td}>{r0(c.cost)}{c.uncosted > 0 && <span title="Some items have no purchase cost" style={{ color: '#b86a00' }}> ⚠</span>}</td>
                    <td style={{ ...S.td, fontWeight: 800, color: c.sales - c.cost < 0 ? '#d63d5b' : '#12905f' }}>{r0(c.sales - c.cost)}</td>
                    <td style={S.td}>{pct(c.sales - c.cost, c.sales)}</td>
                  </tr>
                  {open[c.category] && c.items.map((p) => (
                    <tr key={p.product_id} style={{ background: '#fcfbfe', fontSize: 13 }}>
                      <td style={{ ...S.td, ...left, paddingLeft: 34 }}>{p.name}{p.uncosted && <span style={{ color: '#b86a00', fontWeight: 700 }}> · no cost</span>}</td>
                      <td style={S.td}>{p.weighted ? `${qty(p.qty)} g` : qty(p.qty)}{p.free_qty ? <small style={{ color: '#12905f' }}> +{qty(p.free_qty)} free</small> : null}</td>
                      <td style={{ ...S.td, color: '#5b3fd9', fontWeight: 700 }}>{p.mrp ? (p.weighted ? `${rs2(p.mrp)}/g` : rs2(p.mrp)) : '—'}</td>
                      <td style={S.td}>{r0(p.sales)}</td>
                      <td style={{ ...S.td, color: '#776e86' }}>{(p.qty + (p.free_qty || 0)) > 0 ? `${rs2(p.cost / (p.qty + (p.free_qty || 0)))}${p.weighted ? '/g' : ''}` : '—'}</td>
                      <td style={S.td}>{r0(p.cost)}</td>
                      <td style={{ ...S.td, color: p.sales - p.cost < 0 ? '#d63d5b' : undefined }}>{r0(p.sales - p.cost)}</td>
                      <td style={S.td}>{pct(p.sales - p.cost, p.sales)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#faf8fc' }}>
                <td style={{ ...S.td, ...left, fontWeight: 800 }}>Total (items)</td>
                <td style={S.td} />
                <td style={S.td} />
                <td style={{ ...S.td, fontWeight: 800 }}>{r0(data.totals.sales)}</td>
                <td style={S.td} />
                <td style={{ ...S.td, fontWeight: 800 }}>{r0(data.totals.cost)}</td>
                <td style={{ ...S.td, fontWeight: 800 }}>{r0(data.totals.sales - data.totals.cost)}</td>
                <td style={{ ...S.td, fontWeight: 800 }}>{pct(data.totals.sales - data.totals.cost, data.totals.sales)}</td>
              </tr>
            </tfoot>
          </table>
          {Math.abs(data.totals.bill_adjust) >= 1 && (
            <div style={{ fontSize: 12, color: '#776e86', marginTop: 6 }}>
              Bill discounts and round-off: {data.totals.bill_adjust < 0 ? '−' : '+'}{r0(Math.abs(data.totals.bill_adjust))} (included in the day's Sales above, not in item rows).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
